import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { useLanguage, getPageTexts } from '../i18n/pageTranslations';

const Acreditaciones = () => {
  const lang = useLanguage();
  const t = getPageTexts('acreditaciones', lang);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const colegios = [
    { 
      nombre: 'Colegio Oficial de Médicos de Madrid', 
      numero: 'Nº 282905609',
      logo: '/images/logos/com-madrid.png'
    },
    { 
      nombre: 'Colegio Oficial de Médicos de Barcelona', 
      numero: 'Nº 38059',
      logo: '/images/logos/com-barcelona.png'
    },
  ];

  const sociedades = [
    { 
      nombre: 'SECE', 
      descripcion: 'Sociedad Española de Cirugía Estética',
      logo: '/images/logos/sece.png'
    },
    { 
      nombre: 'SECPRE', 
      descripcion: 'Sociedad Española de Cirugía Plástica, Reparadora y Estética',
      logo: '/images/logos/secpre.png'
    },
    { 
      nombre: 'SEME', 
      descripcion: 'Sociedad Española de Medicina Estética',
      logo: '/images/logos/seme.png'
    },
    { 
      nombre: 'AECEP', 
      descripcion: 'Asociación Española de Cirugía Estética Plástica',
      logo: '/images/logos/aecep.png'
    },
    { 
      nombre: 'ISAPS', 
      descripcion: 'International Society of Aesthetic Plastic Surgery',
      logo: '/images/logos/isaps.png'
    },
  ];

  const marcas = [
    { 
      nombre: 'NewLip®', 
      descripcion: 'Protocolo de rejuvenecimiento perioral',
      registro: 'Registro OEPM',
      web: 'https://newlip.es'
    },
    { 
      nombre: 'Rinomodelación®', 
      descripcion: 'Técnica no quirúrgica de corrección nasal',
      registro: 'Registro OEPM',
      web: 'https://rinomodelacion.com'
    },
  ];

  const backText = {
    es: 'Volver a El Dr. Norro',
    en: 'Back to Dr. Norro',
    fr: 'Retour au Dr. Norro',
    pt: 'Voltar ao Dr. Norro'
  };

  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      
      {/* Hero */}
      <section className="pt-12 pb-16 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl">
          <Link 
            to="/el-doctor"
            className="inline-flex items-center gap-2 text-text-muted hover:text-gold transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            {backText[lang] || backText.es}
          </Link>
          
          <div className="text-center animate-on-scroll">
            <p className="eyebrow mb-6">{t.certifications || 'Credenciales'}</p>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-6">
              {t.title}
            </h1>
            <div className="w-16 h-px bg-gold mx-auto mb-6"></div>
            <p className="text-text-secondary max-w-2xl mx-auto">
              {t.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Colegios Médicos */}
      <section className="py-16 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="mb-10 animate-on-scroll">
            <p className="eyebrow mb-4">Categoría 1</p>
            <h2 className="font-serif text-2xl text-charcoal">Colegios Médicos</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {colegios.map((colegio, index) => (
              <div 
                key={index} 
                className="card-luxury p-8 flex flex-col items-center text-center animate-on-scroll group cursor-pointer hover:border-gold"
              >
                <div className="w-24 h-24 bg-cream flex items-center justify-center mb-6 grayscale group-hover:grayscale-0 transition-all">
                  <span className="text-3xl font-serif text-gold">COM</span>
                </div>
                <h3 className="font-serif text-lg text-charcoal mb-2">{colegio.nombre}</h3>
                <p className="text-gold text-sm">{colegio.numero}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sociedades Científicas */}
      <section className="py-16 bg-cream">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="mb-10 animate-on-scroll">
            <p className="eyebrow mb-4">Categoría 2</p>
            <h2 className="font-serif text-2xl text-charcoal">Sociedades Científicas</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {sociedades.map((sociedad, index) => (
              <div 
                key={index} 
                className="bg-ivory p-6 border border-divider flex flex-col items-center text-center animate-on-scroll group cursor-pointer hover:border-gold transition-all"
              >
                <div className="w-20 h-20 bg-cream flex items-center justify-center mb-4 grayscale group-hover:grayscale-0 transition-all">
                  <span className="text-xl font-serif text-gold">{sociedad.nombre}</span>
                </div>
                <h3 className="font-medium text-charcoal text-sm mb-1">{sociedad.nombre}</h3>
                <p className="text-text-muted text-xs">{sociedad.descripcion}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formación Continuada */}
      <section className="py-16 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="mb-10 animate-on-scroll">
            <p className="eyebrow mb-4">Categoría 3</p>
            <h2 className="font-serif text-2xl text-charcoal">Formación Continuada y Congresos</h2>
          </div>

          <div className="bg-cream p-8 border-l-2 border-gold animate-on-scroll">
            <p className="text-text-secondary leading-relaxed mb-4">
              Participación activa en congresos nacionales e internacionales de cirugía plástica, 
              estética y reparadora. Ponente habitual en eventos de las principales sociedades científicas.
            </p>
            <p className="text-text-muted text-sm">
              Certificados de ponencias, asistencia a congresos y cursos acreditados disponibles bajo solicitud.
            </p>
          </div>
        </div>
      </section>

      {/* Marcas y Patentes */}
      <section className="py-16 bg-charcoal">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="mb-10 animate-on-scroll">
            <p className="eyebrow text-gold mb-4">Categoría 4</p>
            <h2 className="font-serif text-2xl text-ivory">Marcas y Patentes OEPM</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {marcas.map((marca, index) => (
              <div 
                key={index} 
                className="bg-ivory/5 border border-gold/30 p-8 animate-on-scroll hover:border-gold transition-all"
              >
                <h3 className="font-serif text-2xl text-gold mb-2">{marca.nombre}</h3>
                <p className="text-gold-light/80 mb-4">{marca.descripcion}</p>
                <div className="flex items-center justify-between">
                  <span className="text-text-muted text-xs uppercase tracking-wider">{marca.registro}</span>
                  <a 
                    href={marca.web}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold hover:text-gold-light text-sm flex items-center gap-1"
                  >
                    Visitar web <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 border border-gold/20 animate-on-scroll">
            <p className="text-gold-light/70 text-sm text-center">
              <strong className="text-gold">Metasigna:</strong> Patente de conjugados farmacológicos 
              escindibles (Clivamed® y Warcliv®) — pendiente de publicación oficial.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-cream">
        <div className="container mx-auto px-6 max-w-3xl text-center animate-on-scroll">
          <h3 className="font-serif text-3xl text-charcoal mb-4">
            ¿Desea verificar alguna credencial?
          </h3>
          <p className="text-text-secondary mb-10">
            Puede solicitar copias de certificados y documentación acreditativa en la consulta.
          </p>
          <Link 
            to="/contacto"
            className="btn-gold"
          >
            Contactar
          </Link>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Acreditaciones;
